/**
 * A KILL NEVER UN-HAPPENS — so newest-wins must not be allowed to lower it.
 *
 * Reported twice: Greenvale enemies reading as "not beaten" after they had
 * been beaten. That blocks potions, blocks auto-fight, re-applies the
 * first-encounter buff and drops the bestiary count — four visible symptoms
 * from one silent cause.
 *
 * Unreproducible offline through two investigations, because the losing
 * path is not local. `hydrateFromRemote` replaces the ENTIRE local blob
 * with the cloud one whenever the cloud is newer by `lastSeenAt`. A short
 * session on another device IS newer, so its `enemyKills` — which never saw
 * those Greenvale fights — replaces a map that did. That matches the report
 * exactly, including "went away after changing zones" (a later push from
 * this device restored it) and the page reloading itself.
 *
 * ── WHY MERGE RATHER THAN PICK A WINNER ──────────────────────────────────
 * For this field the question is meaningless. `enemyKills` is monotonic:
 * counts only ever rise, so the per-enemy maximum of two copies is correct
 * whichever save is "newer". Merging makes the whole class of loss
 * impossible without having to reproduce the race first — which two
 * attempts failed to do.
 *
 * ── SCOPED TO ONE FIELD, AND GUARDED BY CHARACTER ID ─────────────────────
 * Only `enemyKills`. Other state looks monotonic too — titles, crests,
 * crafted recipes — but merging those would resurrect them after a
 * deliberate character reset performed on another device, which is a worse
 * bug than this one. Even here the merge is gated on both saves carrying
 * the SAME `player.data.id`: a reset mints a new character id, so a reset
 * cloud save replaces cleanly instead of inheriting the old one's kills.
 *
 * Kept free of Redux and browser globals so `.verify` probes can load it.
 */

type KillMap = Record<string, number>

export interface MergeableSave {
  player?: {
    data?: {
      id?: string
      enemyKills?: KillMap
      seenBestiaryCount?: number
    }
  }
}

export interface MergeResult {
  /** The save to write. Same object as `remote` (mutated) or `remote` as-is. */
  save: MergeableSave
  /** Total kills recovered from local that the cloud copy had not seen. */
  recovered: number
  /** Why nothing was merged, for logging. `null` when a merge happened. */
  skipped: 'no-local' | 'no-kill-map' | 'different-character' | 'nothing-to-add' | null
}

/**
 * Fold any kills the local save has that the remote one lacks into `remote`.
 *
 * `remote` is mutated and returned: the caller parsed it, writes it straight
 * to storage, and never reuses it. Returning it too keeps call sites honest
 * about what they are writing.
 */
export function preserveMonotonicKills(
  remote: MergeableSave,
  local: MergeableSave | null,
): MergeResult {
  if (!local) return { save: remote, recovered: 0, skipped: 'no-local' }

  const localKills = local.player?.data?.enemyKills
  const remoteKills = remote.player?.data?.enemyKills
  if (!localKills || !remoteKills) return { save: remote, recovered: 0, skipped: 'no-kill-map' }

  const localId = local.player?.data?.id
  const remoteId = remote.player?.data?.id
  if (!localId || !remoteId || localId !== remoteId) {
    return { save: remote, recovered: 0, skipped: 'different-character' }
  }

  const merged: KillMap = { ...remoteKills }
  let recovered = 0
  for (const [id, n] of Object.entries(localKills)) {
    // A corrupt entry must not become NaN in the merged map and poison a
    // count that was fine.
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) continue
    const have = merged[id] ?? 0
    if (n > have) { merged[id] = n; recovered += n - have }
  }
  if (recovered === 0) return { save: remote, recovered: 0, skipped: 'nothing-to-add' }

  remote.player!.data!.enemyKills = merged
  // Derived from the map by the reducer, so it has to follow the merge or
  // the bestiary count sticks at the cloud save's lower number.
  remote.player!.data!.seenBestiaryCount = Object.keys(merged).length
  return { save: remote, recovered, skipped: null }
}
